import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import SingleDatePicker from '../../../components/form/SingleDatePicker';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ProfileCard from '../../common/ProfileCard';
import ItemDetailsTable from '../../common/ItemDetailsTable';
import pricingEngineForDocuments, {
  buildSubmitLineItemPrice,
  normalizeLineItemPrice,
} from '../../../utils/pricingEngineForDocuments';
import AddLineItemButton from '../../../components/form/AddLineItemButton';
import BusinessProfileSelect from '../../../components/form/BusinessProfileSelect';
import CustomerSelect from '../../../components/form/CustomerSelect';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import TopBar from '../../../components/TopBar';
import { applyApiErrors } from '../../../utils/apiFieldErrors';
import DocumentCurrencyInput from '../../../components/form/DocumentCurrencyInput';
import CustomTextInput from '../../../components/CustomTextInput';
import DocumentSummarySection from '../../../components/DocumentSummarySection';
import { Refresh as RefreshIcon, Timeline as TimelineIcon } from '@mui/icons-material';
import DocumentDetailedPeppolStatus from '../../common/DocumentDetailedPeppolStatus';
import CopyTextComponent from '../../../components/CopyTextComponent';
import CNReasonCodeSelect from './CNReasonCodeSelect';
import { getCreditNoteValidationSchema } from '../../../validations/CreditNotesValidation';
import DocumentTimeline from '../../common/DocumentTimeline';
import { useSelector } from 'react-redux';
import DocumentLevelChargeAndAllowancesButton from '../../common/DocumentLevelChargeAndAllowancesButton';
import {
  CREDIT_NOTE_DOCUMENT_TYPE_CODE,
  DEFAULT_CURRENCY_CODE,
  DEFAULT_DOCUMENT_SOURCE,
  DEFAULT_TRANSITION_TYPE_CODE,
  SELF_BILLED_CREDIT_NOTE_DOCUMENT_TYPE_CODE,
  UAE_COUNTRY_CODE,
} from '../../../constants/defaults';
import { getCountryOptionByCode, getSubdivisionOptionByCode } from '../../../api/codelist/codeListsSlice';
import { getAllFilesTotalSize, transactionTypePositionCheck } from '../../../utils/helper';
import { buildInvoicePeriodPayload } from '../../../utils/invoicePeriodPayload';
import TransactionTypeFields from '../../../components/form/TransactionTypeFields';
import { B2C_ELECTRONIC_ADDRESS } from '../../../constants/appConstants';
import { deriveStatusFromMetadata } from '../../../utils/derivePeppolStatus';
import { useNavigate } from 'react-router-dom';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { useRevalidateFormOnLanguageChange } from '../../../hooks/useRevalidateFormOnLanguageChange';
import DocumentTypeSelect from '../../../components/form/DocumentTypeSelect';
import SupplierProfileSelect from '../../../components/form/SupplierProfileSelect';
import { useAutoPopulateForm } from '../../../hooks/useAutoPopulateForm';
import MultipleFileUpload from '../../Invoices/components/MultipleFileUpload';
import AttachmentViewer from '../../Invoices/components/AttachmentViewer';
import { formatFileSize } from '../../../utils/formatFileSize';
import { nowInGulfTimeString } from '../../../utils/formatDate';
import DocumentNoteField from '../../common/DocumentNoteField';

export default function CreditNoteForm({
  initialData = {},
  onSubmit,
  onCancel,
  readOnly = false,
  apiErrors = {},
  isPurchase,
  isResubmit = false,
  isLoading = false,
  isLinkedFlow = false,
}) {
  const [__, setChargeEditing] = useState(false);
  const [_, setAllowanceEditing] = useState(false);
  const { settings } = useSelector((state) => state.org.org);
  const billingRefs = initialData?.billing_reference ?? [];
  const linkedInvoiceReferenceDefaultsRef = useRef({
    invoice_reference_id: billingRefs.map((ref) => ref?.id).filter((id) => id && id.trim() !== ''),
    invoice_reference_date: billingRefs[0]?.issue_date ? dayjs(billingRefs[0].issue_date) : null,
  });
  const [inputValue, setInputValue] = useState('');
  const isFieldLocked = readOnly || isLinkedFlow;
  const [profilesData, setProfilesData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [rawFiles, setRawFiles] = useState([]);

  const navigate = useNavigate();

  const { eInvoiceForm, validations, language, common } = useTranslatedMessages();
  const validationSchema = useMemo(
    () => getCreditNoteValidationSchema(validations, { isPurchase }),
    [validations, isPurchase],
  );
  const { credit_note_page, credit_note_top_bar, buttons, credit_note_form, cards, document_note } = eInvoiceForm;

  const backAction = () => {
    if (readOnly) {
      navigate(-1);
    } else {
      onCancel();
    }
  };

  const countryOption = useSelector(
    getCountryOptionByCode(initialData.delivery?.delivery_location?.address?.country_code),
  );
  const subdivisionOption = useSelector(
    getSubdivisionOptionByCode(initialData.delivery?.delivery_location?.address?.country_subentity),
  );

  const defautCreditNoteTypeCode = isPurchase
    ? SELF_BILLED_CREDIT_NOTE_DOCUMENT_TYPE_CODE
    : CREDIT_NOTE_DOCUMENT_TYPE_CODE;

  const {
    control,
    getValues,
    setValue,
    handleSubmit,
    setError,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    context: {
      auto_numbering_enabled: settings?.auto_numbering_enabled ?? false,
      credit_note_type_code: initialData?.credit_note_type_code ?? defautCreditNoteTypeCode,
    },
    mode: 'onChange',
    defaultValues: {
      credit_note_type_code: initialData?.credit_note_type_code ?? defautCreditNoteTypeCode,
      profile_execution_id: initialData?.profile_execution_id || DEFAULT_TRANSITION_TYPE_CODE,
      document_source: DEFAULT_DOCUMENT_SOURCE,

      discrepancy_response: initialData?.discrepancy_response || '',
      document_number: initialData?.document_number || '',
      note: initialData?.note || '',
      issue_date: dayjs(initialData?.issue_date) || null,
      issue_time: nowInGulfTimeString(),

      invoice_reference_id: billingRefs.map((ref) => ref?.id).filter((id) => id && id.trim() !== ''),
      invoice_reference_date: billingRefs[0]?.issue_date ? dayjs(billingRefs[0].issue_date) : null,

      reason_code: initialData?.reason_code || '',

      document_lines: (initialData?.document_lines ?? []).map((item, index) => ({
        ...item,
        key: item?.key || `ITM-${index}`,
        ...(item.price && { price: normalizeLineItemPrice(item.price) }),
      })),

      document_currency_code: initialData?.document_currency_code || 'AED',
      tax_exchange_rate: initialData?.tax_exchange_rate || null,
      accounting_supplier_party: initialData?.accounting_supplier_party || null,
      accounting_customer_party: initialData?.accounting_customer_party || null,
      charges: initialData?.charges || [],
      allowances: initialData?.allowances || [],

      invoice_period_start_date: initialData?.invoice_period?.start_date
        ? dayjs(initialData.invoice_period.start_date)
        : null,
      invoice_period_end_date: initialData?.invoice_period?.end_date
        ? dayjs(initialData.invoice_period.end_date)
        : null,
      invoice_period_description: initialData?.invoice_period?.description || null,

      principle_id_trn: initialData?.seller_supplier_party?.id || null,
      beneficiary_id: initialData?.buyer_customer_party?.id || null,
      delivery_party_name: initialData?.delivery?.party_name || null,
      incoterms: initialData?.delivery?.terms || null,

      delivery_street_name: initialData?.delivery?.delivery_location?.address?.street_name || null,
      delivery_city_name: initialData?.delivery?.delivery_location?.address?.city_name || null,
      delivery_country_code: initialData?.delivery?.delivery_location?.address?.country_code ? countryOption : null,
      delivery_country_subentity:
        countryOption?.code === UAE_COUNTRY_CODE
          ? (subdivisionOption ?? null)
          : (initialData.delivery?.delivery_location?.address?.country_subentity ?? null),

      delivery_postal_zone: initialData?.delivery?.delivery_location?.address?.postal_zone || null,
    },
  });
  useEffect(() => {
    applyApiErrors(setError, apiErrors);
  }, [setError, apiErrors]);

  useRevalidateFormOnLanguageChange(language, trigger, errors);

  const {
    fields: lineItems,
    replace: replaceLineItems,
    append: appendLineItem,
    remove: removeLineItem,
  } = useFieldArray({
    control,
    name: 'document_lines',
  });

  const { remove: chargeRemove } = useFieldArray({
    control: control,
    name: 'charges',
  });

  const { remove: allowanceRemove } = useFieldArray({
    control: control,
    name: 'allowances',
  });

  const handleSetBilledTo = (data) => {
    setValue('accounting_supplier_party', data);
  };

  const handleAttachmentUpload = (attachmentArray) => {
    setAttachments(attachmentArray);
  };

  const handleRawFiles = (fileList) => {
    setRawFiles(fileList);
  };

  const creditNoteTypeCode = useWatch({ control, name: 'credit_note_type_code' });

  useAutoPopulateForm({
    profiles: profilesData,
    customers: customerData,
    suppliers: supplierData,
    documentType: isPurchase ? 'PURCHASE' : 'SALES',
    setValue,
    getValues,
    invoiceTypeCode: creditNoteTypeCode,
  });

  const billedBy = useWatch({ control, name: 'accounting_supplier_party' });
  const billedTo = useWatch({ control, name: 'accounting_customer_party' });
  const document_lines = useWatch({ control, name: 'document_lines' }) || [];
  const docCharges = useWatch({ control, name: 'charges' }) || [];
  const docAllowances = useWatch({ control, name: 'allowances' }) || [];
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);

  const handleTimelineOpen = () => setIsTimelineOpen(true);
  const handleTimelineClose = () => setIsTimelineOpen(false);

  const conversionRate = useWatch({ control, name: 'tax_exchange_rate.calculation_rate' });

  const documentDate = useWatch({ control, name: 'issue_date' });
  const profileExecutionId = useWatch({ control, name: 'profile_execution_id' });

  useEffect(() => {
    trigger(['document_lines', 'allowances', 'charges']);
  }, [profileExecutionId, trigger]);

  const discrepancyResponse = useWatch({ control, name: 'discrepancy_response' });
  const isVolumeDiscount = discrepancyResponse === 'VD';
  const isInvoiceReferenceDisabled = isFieldLocked || isVolumeDiscount;
  const previousCreditNoteTypeCodeRef = useRef(creditNoteTypeCode);

  useEffect(() => {
    if (readOnly) {
      return;
    }

    if (discrepancyResponse === 'VD') {
      setValue('invoice_reference_id', []);
      setValue('invoice_reference_date', null);
      setInputValue('');
      return;
    }

    if (isLinkedFlow) {
      const defaults = linkedInvoiceReferenceDefaultsRef.current;
      setValue('invoice_reference_id', defaults.invoice_reference_id, { shouldValidate: true });
      setValue('invoice_reference_date', defaults.invoice_reference_date, { shouldValidate: true });
    }
  }, [discrepancyResponse, readOnly, isLinkedFlow, setValue]);

  useEffect(() => {
    const previousCreditNoteTypeCode = previousCreditNoteTypeCodeRef.current;
    if (!previousCreditNoteTypeCode || previousCreditNoteTypeCode === creditNoteTypeCode) {
      previousCreditNoteTypeCodeRef.current = creditNoteTypeCode;
      return;
    }

    setValue('accounting_supplier_party', null, { shouldValidate: true });
    setValue('profile_execution_id', DEFAULT_TRANSITION_TYPE_CODE, { shouldValidate: true });

    const resetDocumentLines = (getValues('document_lines') || []).map((line) => ({
      ...line,
      classified_tax_category: {
        id: null,
        percent: 0,
        tax_exemption_reason_code: null,
        tax_exemption_reason: null,
        tax_scheme: line?.classified_tax_category?.tax_scheme || 'VAT',
      },
    }));
    setValue('document_lines', resetDocumentLines, { shouldValidate: true });

    const resetDocLevelTaxCategory = (records) =>
      (records || []).map((record) => ({
        ...record,
        tax_category: {
          id: null,
          percent: 0,
          tax_exemption_reason_code: null,
          tax_exemption_reason: null,
          tax_scheme: record?.tax_category?.tax_scheme || 'VAT',
        },
      }));

    setValue('allowances', resetDocLevelTaxCategory(getValues('allowances')), { shouldValidate: true });
    setValue('charges', resetDocLevelTaxCategory(getValues('charges')), { shouldValidate: true });
    trigger(['accounting_supplier_party', 'profile_execution_id', 'document_lines', 'allowances', 'charges']);

    previousCreditNoteTypeCodeRef.current = creditNoteTypeCode;
  }, [creditNoteTypeCode, getValues, setValue, trigger]);

  useEffect(() => {
    // Re-trigger due_date validation when document_date changes
    trigger('due_date');
  }, [documentDate]);

  const handleFormSubmit = async (data) => {
    // Transform document_lines to ensure proper price structure
    const transformedDocumentLines = data.document_lines?.map((line) => ({
      ...line,
      price: buildSubmitLineItemPrice(line.price),
    }));

    const shouldSendDeliveryData =
      transactionTypePositionCheck(data.profile_execution_id, 7) ||
      transactionTypePositionCheck(data.profile_execution_id, 8);

    const shouldSendInvoicePeriodData = transactionTypePositionCheck(data.profile_execution_id, 4);
    const shouldSendPrincipleIdTrnData = transactionTypePositionCheck(data.profile_execution_id, 6);
    const shouldSendBeneficiaryId = transactionTypePositionCheck(data.profile_execution_id, 1);
    const invoiceIds = Array.isArray(data.invoice_reference_id)
      ? data.invoice_reference_id
      : data.invoice_reference_id
        ? [data.invoice_reference_id]
        : [];

    const payload = {
      ...data,
      document_lines: transformedDocumentLines,
      due_date: dayjs(data.due_date).format('YYYY-MM-DD'),
      issue_date: dayjs(data.issue_date).format('YYYY-MM-DD'),
      issue_time: nowInGulfTimeString(),
      ...(data.discrepancy_response !== 'VD' && {
        billing_reference: invoiceIds.map((id) => ({
          id,
          issue_date: data.invoice_reference_date ? dayjs(data.invoice_reference_date).format('YYYY-MM-DD') : null,
        })),
      }),
      sub_total: subtotal,
      total_excluding_tax: totalExcludingTax,
      total_tax: totalTaxAmount,
      total_including_tax: totalIncludingTax,
      total_payable: totalPayableAmount,
      header_allowance: headerAllowance,
      header_charges: headerCharges,

      total_paid: 0,
      ...(data?.document_currency_code !== DEFAULT_CURRENCY_CODE && {
        tax_currency_code: DEFAULT_CURRENCY_CODE,
      }),

      ...(shouldSendPrincipleIdTrnData && {
        seller_supplier_party: {
          id: data?.principle_id_trn,
        },
      }),
      ...(shouldSendBeneficiaryId && {
        buyer_customer_party: {
          id: data?.beneficiary_id,
        },
      }),
      ...(shouldSendInvoicePeriodData && {
        invoice_period: buildInvoicePeriodPayload(data),
      }),
      ...(shouldSendDeliveryData && {
        delivery: {
          party_name: data?.delivery_party_name,
          terms: data?.incoterms,
          delivery_location: {
            address: {
              street_name: data?.delivery_street_name,
              city_name: data?.delivery_city_name,
              country_code: data?.delivery_country_code?.code,
              country: data?.delivery_country_code?.label,
              country_subentity:
                data?.delivery_country_code?.code === UAE_COUNTRY_CODE
                  ? data?.delivery_country_subentity?.code
                  : data?.delivery_country_subentity,
              postal_zone: data?.delivery_postal_zone,
            },
          },
        },
      }),
      ...(attachments && {
        attachments: [...attachments],
      }),
    };
    onSubmit(payload);
  };

  const {
    itemsSubtotal,
    subtotal,
    totalTaxAmount,
    taxInAED,
    totalItemAllowances,
    totalExcludingTax,
    totalIncludingTax,
    totalItemCharges,
    totalPayableAmount,
    totalTaxableAmount,
    totalNonTaxableAmount,
    taxBreakdown,
    headerCharges,
    headerAllowance,
    payableRoundingAmount,
  } = pricingEngineForDocuments({
    items: document_lines,
    docCharges: docCharges,
    docAllowances: docAllowances,
    conversionRatePerAED: conversionRate,
    ...(readOnly &&
      initialData?.payable_rounding_amount != null && {
        payableRoundingAmount: initialData.payable_rounding_amount,
      }),
  });

  const isB2CCustomer = initialData?.accounting_customer_party?.endpoint_id === B2C_ELECTRONIC_ADDRESS;

  const derivedStatus = deriveStatusFromMetadata(
    initialData?.meta_info,
    isPurchase,
    isB2CCustomer,
    initialData?.document_source,
  );
  const rawInvoiceRef = useWatch({
    control,
    name: 'invoice_reference_id',
  });

  const invoiceRefs = (Array.isArray(rawInvoiceRef) ? rawInvoiceRef : rawInvoiceRef ? [rawInvoiceRef] : []).filter(
    (ref) => ref && ref.trim() !== '',
  );
  let message = errors?.invoice_reference_id?.message ?? '';
  if (message == '' && !isInvoiceReferenceDisabled) message = credit_note_form.invoice_reference_helper_enter_to_add;
  return (
    <>
      <Box className="InvoiceDetails" sx={{ pt: 2, pb: 4 }}>
        <Grid
          container
          sx={{
            display: 'block',
          }}
        >
          <Grid>
            <TopBar
              title={
                <Stack direction="row" gap={3}>
                  {readOnly ? (
                    <>
                      {isPurchase
                        ? credit_note_top_bar.self_billed_credit_note_details
                        : credit_note_top_bar.credit_note_details}{' '}
                      <CopyTextComponent copyText={initialData.id} />{' '}
                    </>
                  ) : isPurchase ? (
                    credit_note_page.title_purchase
                  ) : (
                    credit_note_page.title_sales
                  )}
                </Stack>
              }
            >
              <Stack direction="row" spacing={1}>
                {readOnly && (
                  <>
                    <Button
                      data-testid="refresh-button"
                      variant="outlined"
                      color="primary"
                      onClick={() => window.location.reload()}
                    >
                      <RefreshIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                      Refresh
                    </Button>
                    {initialData?.is_phase2_document && (
                      <Button
                        data-testid="timeline-button"
                        variant="outlined"
                        color="primary"
                        onClick={handleTimelineOpen}
                      >
                        <TimelineIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                        {buttons.timeline}
                      </Button>
                    )}
                  </>
                )}
                <Button
                  data-testid="cancel-button"
                  variant={readOnly ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={backAction}
                  disabled={isLoading}
                >
                  {readOnly ? buttons.back : buttons.cancel}
                </Button>

                {!readOnly && (
                  <Button
                    data-testid="submit-button"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(handleFormSubmit)}
                    disabled={isLoading}
                  >
                    {isResubmit ? buttons.resubmit_to_fta : buttons.submit}
                  </Button>
                )}
              </Stack>
            </TopBar>
          </Grid>
        </Grid>
      </Box>

      {(readOnly || isResubmit) && (
        <>
          {initialData?.is_phase2_document && (
            <DocumentDetailedPeppolStatus
              documentType="credit-notes"
              docId={initialData.id}
              metaInfo={initialData?.meta_info}
              isPurchase={isPurchase}
              buyer={initialData?.buyer}
              documentSource={initialData?.document_source}
            />
          )}
          {initialData?.is_phase2_document && (
            <DocumentTimeline
              documentType="credit-notes"
              docId={initialData.id}
              isPurchase={isPurchase}
              isOpen={isTimelineOpen}
              onClose={handleTimelineClose}
            />
          )}
        </>
      )}

      <Box className="CreditNoteDetails">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <CustomTextInput
              data-testid="document-number-input"
              label={credit_note_form.credit_note_number_label}
              control={control}
              errors={errors}
              name={'document_number'}
              readOnly={readOnly || isResubmit}
              disabled={settings?.auto_numbering_enabled || isResubmit}
              helperText={settings?.auto_numbering_enabled ? credit_note_form.auto_numbering_helper : ''}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SingleDatePicker
              data-testid="credit-note-date-input"
              name="issue_date"
              label={credit_note_form.credit_note_issue_date_label}
              control={control}
              errors={errors}
              readOnly={isFieldLocked}
            />
          </Grid>{' '}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label={credit_note_form.invoice_reference_id_label}
              placeholder={invoiceRefs.length === 0 ? credit_note_form.invoice_reference_search_placeholder : ''}
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  let value = inputValue.trim().replace(/"/g, '');
                  if (!value) return;
                  const updated = [...invoiceRefs, value];
                  const unique = [...new Set(updated)];
                  setValue('invoice_reference_id', unique, { shouldValidate: true });
                  setInputValue('');
                }
              }}
              disabled={isInvoiceReferenceDisabled}
              error={errors?.invoice_reference_id}
              helperText={message}
              sx={{
                '& .MuiOutlinedInput-root': {
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 14px',
                  minHeight: '40px',
                  height: 'auto',
                },
                '& .MuiOutlinedInput-input': {
                  padding: '4px 0',
                  minWidth: '80px',
                  flex: 1,
                },
              }}
              InputProps={{
                startAdornment:
                  invoiceRefs.length > 0
                    ? invoiceRefs.map((ref, index) => (
                        <Chip
                          key={index}
                          label={ref}
                          size="small"
                          onDelete={
                            isInvoiceReferenceDisabled
                              ? undefined
                              : () => {
                                  const updated = invoiceRefs.filter((_, i) => i !== index);
                                  setValue('invoice_reference_id', updated, { shouldValidate: true });
                                }
                          }
                        />
                      ))
                    : null,
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <SingleDatePicker
              data-testid="credit-note-due-date-input"
              name="invoice_reference_date"
              label={credit_note_form.invoice_reference_date_label}
              control={control}
              errors={errors}
              readOnly={isFieldLocked}
              disabled={isInvoiceReferenceDisabled}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <DocumentTypeSelect
              data-testid="credit-note-type-select"
              control={control}
              errors={errors}
              readOnly={isFieldLocked}
              isPurchase={isPurchase}
              documentType="creditNote"
              name="credit_note_type_code"
              label={common.document_type_select.credit_note_type_label}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 8 }}>
            <CNReasonCodeSelect
              data-testid="credit-reason-select"
              name={`discrepancy_response`}
              control={control}
              errors={errors}
              readOnly={readOnly}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={2}>
          <TransactionTypeFields
            control={control}
            errors={errors}
            readOnly={isFieldLocked}
            profileExecutionId={profileExecutionId}
            setValue={setValue}
            trigger={trigger}
            invoiceTypeCode={creditNoteTypeCode}
          />
        </Grid>

        <Grid container spacing={2} mt={2} alignItems="stretch">
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            {isPurchase ? (
              <SupplierProfileSelect
                data-testid="billed-by-select"
                label={cards.billed_by_label}
                name="accounting_supplier_party"
                control={control}
                errors={errors}
                readOnly={isFieldLocked}
                onSupplierLoaded={setSupplierData}
              />
            ) : (
              <BusinessProfileSelect
                data-testid="billed-by-select"
                label={cards.billed_by_label}
                name="accounting_supplier_party"
                control={control}
                errors={errors}
                readOnly={isFieldLocked || isResubmit}
                onProfilesLoaded={setProfilesData}
                invoiceTypeCode={creditNoteTypeCode}
              />
            )}
            {billedBy && (
              <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ProfileCard
                  title={cards.billed_by_title}
                  subtitle={cards.billed_by_subtitle}
                  document={{
                    ...billedBy,
                  }}
                />
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', flexDirection: 'column' }}>
            {isPurchase ? (
              <BusinessProfileSelect
                data-testid="billed-to-select"
                label={cards.billed_to_label}
                name="accounting_customer_party"
                control={control}
                errors={errors}
                readOnly={isFieldLocked || isResubmit}
                onProfilesLoaded={setProfilesData}
              />
            ) : (
              <CustomerSelect
                data-testid="billed-to-select"
                label={cards.billed_to_label}
                name="accounting_customer_party"
                control={control}
                errors={errors}
                readOnly={isFieldLocked}
                onSelect={handleSetBilledTo}
                setError={setError}
                onCustomerLoaded={setCustomerData}
              />
            )}
            {billedTo && (
              <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <ProfileCard
                  title={cards.billed_to_title}
                  subtitle={cards.billed_to_subtitle}
                  rawData={billedTo}
                  readOnly={isFieldLocked}
                  onUpdate={handleSetBilledTo}
                  document={{
                    ...billedTo, // Use the selected value from the form state
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Grid container sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Grid>
                {!readOnly && (
                  <AddLineItemButton
                    errors={errors}
                    addItem={appendLineItem}
                    lineItems={lineItems}
                    invoiceTypeCode={creditNoteTypeCode}
                    profileExecutionId={profileExecutionId}
                  />
                )}
              </Grid>

              <Grid container spacing={2} direction="row" alignItems="center" marginBottom={'1.2em'}>
                <DocumentCurrencyInput
                  control={control}
                  errors={errors}
                  documentCurrencyCode={useWatch({
                    control,
                    name: 'document_currency_code',
                  })}
                  readOnly={isFieldLocked}
                />
              </Grid>
            </Grid>
            <ItemDetailsTable
              items={lineItems}
              remove={removeLineItem}
              replace={replaceLineItems}
              add={appendLineItem}
              readOnly={readOnly}
              invoiceTypeCode={creditNoteTypeCode}
              profileExecutionId={profileExecutionId}
              errors={errors}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid size={{ md: 6 }}>
            <DocumentLevelChargeAndAllowancesButton
              control={control}
              readOnly={readOnly}
              currentNetAmount={totalExcludingTax}
              itemBaseAmount={totalExcludingTax}
              invoiceTypeCode={creditNoteTypeCode}
              profileExecutionId={profileExecutionId}
              errors={errors}
            />
          </Grid>
          <Grid size={{ md: 6 }}>
            <DocumentSummarySection
              itemsSubtotal={itemsSubtotal}
              subtotal={subtotal}
              totalItemAllowances={totalItemAllowances}
              totalItemCharges={totalItemCharges}
              totalExcludingTax={totalExcludingTax}
              totalIncludingTax={totalIncludingTax}
              totalTaxAmount={totalTaxAmount}
              taxInAED={taxInAED}
              totalPayableAmount={totalPayableAmount}
              documentCurrencyCode={getValues('document_currency_code')}
              headerCharges={headerCharges}
              headerAllowance={headerAllowance}
              totalTaxableAmount={totalTaxableAmount}
              totalNonTaxableAmount={totalNonTaxableAmount}
              taxBreakdown={taxBreakdown}
              payableRoundingAmount={payableRoundingAmount}
            />
          </Grid>
        </Grid>

        <DocumentNoteField
          control={control}
          errors={errors}
          readOnly={readOnly}
          value={initialData?.note}
          messages={document_note}
        />

        {!readOnly && (
          <Grid container spacing={2} sx={{ my: 2 }}>
            <Grid size={{ md: 6 }}>
              <Grid container direction="column" alignItems="space-between" sx={{ mt: 2 }}>
                <Grid>
                  <Typography variant="h6" fontWeight="light-bold">
                    Supporting Documents
                  </Typography>
                </Grid>
                <Grid>
                  <Typography variant="body2" fontWeight="light">
                    Attach reference files({formatFileSize(getAllFilesTotalSize(rawFiles))} of 7.0 MB used)
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <MultipleFileUpload onUpload={handleAttachmentUpload} onRawFileUpload={handleRawFiles} />
            </Grid>
          </Grid>
        )}

        {readOnly && initialData?.attachments && (
          <AttachmentViewer
            attachments={initialData?.attachments}
            docId={initialData?.id}
            isPurchase={isPurchase}
            isCreditNote={true}
          />
        )}
      </Box>
    </>
  );
}
